import { Injectable } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { Member } from '../../models/member.model';
import {
    APPOINTMENT_TYPES,
    ROLES,
} from '../../constants/appointment.constants';

/**
 * PermissionService - Centralized role-based access control
 *
 * This service provides a single source of truth for all permission checks
 * throughout the application, eliminating duplicated role-checking logic.
 *
 * Usage:
 * ```typescript
 * constructor(private permissions: PermissionService) {}
 *
 * if (this.permissions.hasRole(ROLES.COMITE)) {
 *   // Show admin features
 * }
 *
 * if (this.permissions.hasFullAccessToAppointment(appointmentName)) {
 *   // Show QR code and stats
 * }
 * ```
 */
@Injectable({
    providedIn: 'root',
})
export class PermissionService {
    constructor(private auth: AuthService) {}

    /**
     * Check if the current user has a specific role
     *
     * @param roleName - Role to check (use ROLES constants)
     * @returns true if user has the role, false otherwise
     *
     * @example
     * if (permissions.hasRole(ROLES.COMITE)) {
     *   // User is in committee
     * }
     */
    hasRole(roleName: string): boolean {
        const user = this.auth.getCurrentUser();
        return user?.roles?.includes(roleName) ?? false;
    }

    /**
     * Check if current user has multiple roles (any match)
     *
     * @param roleNames - Array of roles to check
     * @returns true if user has ANY of the specified roles
     *
     * @example
     * if (permissions.hasAnyRole([ROLES.COMITE, ROLES.CHEF_CHAR])) {
     *   // User is either committee or chef char
     * }
     */
    hasAnyRole(roleNames: string[]): boolean {
        const user = this.auth.getCurrentUser();
        if (!user?.roles) return false;

        return roleNames.some((role) => user.roles!.includes(role));
    }

    /**
     * Check if current user has all specified roles
     *
     * @param roleNames - Array of roles to check
     * @returns true if user has ALL of the specified roles
     */
    hasAllRoles(roleNames: string[]): boolean {
        const user = this.auth.getCurrentUser();
        if (!user?.roles) return false;

        return roleNames.every((role) => user.roles!.includes(role));
    }

    /**
     * Check if user has full access to an appointment
     *
     * Full access means:
     * - Can view QR code
     * - Can view statistics
     * - Can manage appointment details
     *
     * Access rules:
     * - Comité: Full access to ALL appointments
     * - Chef char: Full access to "Char" and "Char et costume"
     * - Chef costume: Full access to "Costume" and "Char et costume"
     * - Membre: No full access (read-only)
     *
     * @param appointmentName - Name/type of the appointment
     * @returns true if user has full access to this appointment type
     *
     * @example
     * if (permissions.hasFullAccessToAppointment(APPOINTMENT_TYPES.CHAR)) {
     *   // Show QR code button
     * }
     */
    hasFullAccessToAppointment(appointmentName: string): boolean {
        const user = this.auth.getCurrentUser();
        if (!user) return false;

        const roles = user.roles ?? [];

        // Comité has full access to everything
        if (roles.includes(ROLES.COMITE)) {
            return true;
        }

        // Chef char has full access to Char and Char et costume appointments
        if (roles.includes(ROLES.CHEF_CHAR)) {
            if (
                appointmentName === APPOINTMENT_TYPES.CHAR ||
                appointmentName === APPOINTMENT_TYPES.CHAR_ET_COSTUME
            ) {
                return true;
            }
        }

        // Chef costume has full access to Costume and Char et costume appointments
        if (roles.includes(ROLES.CHEF_COSTUME)) {
            if (
                appointmentName === APPOINTMENT_TYPES.COSTUME ||
                appointmentName === APPOINTMENT_TYPES.CHAR_ET_COSTUME
            ) {
                return true;
            }
        }

        // Chef premier mai has full access to Répétition premier mai and Premier mai
        if (roles.includes(ROLES.CHEF_PREMIER_MAI)) {
            if (
                appointmentName === APPOINTMENT_TYPES.REPETITION_PREMIER_MAI ||
                appointmentName === APPOINTMENT_TYPES.PREMIER_MAI
            ) {
                return true;
            }
        }

        return false;
    }

    /**
     * Get the current user
     *
     * @returns Current member or null if not authenticated
     */
    getCurrentUser(): Member | null {
        return this.auth.getCurrentUser();
    }

    /**
     * Check if user is authenticated
     *
     * @returns true if user is logged in
     */
    isAuthenticated(): boolean {
        return this.auth.getCurrentUser() !== null;
    }

    /**
     * Get all roles for the current user
     *
     * @returns Array of role names, or empty array if no user
     */
    getUserRoles(): string[] {
        return this.auth.getCurrentUser()?.roles ?? [];
    }

    /**
     * Check if user is a committee member (Comité)
     *
     * @returns true if user has Comité role
     */
    isComite(): boolean {
        return this.hasRole(ROLES.COMITE);
    }

    /**
     * Check if user is a regular member (Membre)
     *
     * @returns true if user has Membre role
     */
    isMembre(): boolean {
        return this.hasRole(ROLES.MEMBRE);
    }

    /**
     * Check if user is Chef char
     *
     * @returns true if user has Chef char role
     */
    isChefChar(): boolean {
        return this.hasRole(ROLES.CHEF_CHAR);
    }

    /**
     * Check if user is Chef costume
     *
     * @returns true if user has Chef costume role
     */
    isChefCostume(): boolean {
        return this.hasRole(ROLES.CHEF_COSTUME);
    }

    /**
     * Check if user is Chef premier mai
     *
     * @returns true if user has Chef premier mai role
     */
    isChefPremierMai(): boolean {
        return this.hasRole(ROLES.CHEF_PREMIER_MAI);
    }
}
